import { useEffect, useState } from 'react';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import Typo from 'consts/typography';
import { shortenAddress } from 'utils/commonUtils';
import { FaCheck, FaCopy } from 'react-icons/fa';
import StakingForm from '../Staking/StakingForm';
import ClaimForm from '../Staking/ClaimForm';
import { platformStates } from 'redux/platformSlice';
import StakingFormOffChain from '../Staking/StakingFormOffChain';
import stakingApi from 'api/stakingApi';
import { StakingVaultResponse } from 'models/stake';

interface StakingProps {
  redirectToHistory: () => void;
}

const textButtons = [
  {
    value: 1,
    label: 'On-Chain',
  },
  {
    value: 2,
    label: 'On Terabet',
  },
];

const TOKEN_ADDRESS = process.env.REACT_APP_BET_TOKEN_ADDRESS;
const Staking: React.FunctionComponent<StakingProps> = ({ redirectToHistory }) => {
  const t = useAppTranslation();
  const [copied, setCopied] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(1);
  const platform = useAppSelector(platformStates);
  const [vaults, setVaults] = useState<StakingVaultResponse[] | null>(null);

  const handleWriteClipboardText = async () => {
    await navigator.clipboard.writeText(TOKEN_ADDRESS as string);
    setCopied(TOKEN_ADDRESS as string);
    setTimeout(() => {
      setCopied(null);
    }, 3000);
  };

  const handleShowLoading = (showLoading: boolean) => {
    setIsLoading(showLoading);
  };

  const onChangeActiveTab = (value: number) => {
    setActiveTab(value);
  };

  const getVaults = async () => {
    const { ok, body } = await stakingApi.getStakingVaults();
    if (ok && body) {
      const vaultsResponse = body.sort((a, b) => Number(a.dayNumber) - Number(b.dayNumber));
      setVaults(vaultsResponse);
    }
  };

  useEffect(() => {
    getVaults();
  }, []);

  return (
    <div className="w-100 staking-container">
      <div className="tbet-info">
        <div className="tbet-row">
          <div className="tbet-col">
            <h6 className={`${Typo.p5} primary-text-color`}>{t('staking.info.name')}</h6>
            <span className={`${Typo.p3}`}>Terabet Token</span>
          </div>
          <div className="tbet-col">
            <h6 className={`${Typo.p5} primary-text-color`}>{t('staking.info.symbol')}</h6>
            <span className={`${Typo.p3}`}>$TBET</span>
          </div>
          <div className="tbet-col">
            <h6 className={`${Typo.p5} primary-text-color`}>{t('staking.info.type')}</h6>
            <span className={`${Typo.p3}`}>BEP-20 (Binance Smart Chain)</span>
          </div>
        </div>
        <div className="tbet-row">
          <div className="tbet-col">
            <h6 className={`${Typo.p5} primary-text-color`}>{t('staking.info.address')}</h6>
            <span className={`${Typo.p3}`}>{shortenAddress(TOKEN_ADDRESS)} </span>
            {copied === TOKEN_ADDRESS ? (
              <FaCheck width={20} />
            ) : (
              <FaCopy width={20} onClick={handleWriteClipboardText} />
            )}
          </div>
          <div className="tbet-col">
            <h6 className={`${Typo.p5} primary-text-color`}>{t('staking.info.dex')}</h6>
            <span className={`${Typo.p3}`}>
              <a
                href="https://app.uniswap.org/swap?chain=bnb&inputCurrency=0x55d398326f99059ff775485246999027b3197955&outputCurrency=0x59caf3854a29d886080fe28dfecb2dec61cf5ba2"
                target="_blank"
                rel="noreferrer"
              >
                Uniswap
              </a>
              <br />
              <a
                href="https://pancakeswap.finance/swap?inputCurrency=0x55d398326f99059fF775485246999027B3197955&outputCurrency=0x59caf3854A29D886080Fe28DfECb2DEc61cF5BA2"
                target="_blank"
                rel="noreferrer"
              >
                Pancakeswap
              </a>
            </span>
          </div>
          <div className="tbet-col">
            <h6 className={`${Typo.p5} primary-text-color`}>{t('staking.info.decimals')}</h6>
            <span className={`${Typo.p3}`}>18</span>
          </div>
        </div>
      </div>
      <div className="w-full flex flex-row items-center justify-between staking-tab">
        {textButtons.map((x) => (
          <button
            key={`my-bet-tab-${x.value}`}
            className={`cursor-pointer ${
              platform.isDesktop || platform.isLargeDesktop ? Typo.p1 : Typo.p3
            } ${activeTab === x.value ? 'active' : ''}`}
            onClick={() => onChangeActiveTab(x.value)}
          >
            {x.label}
          </button>
        ))}
      </div>

      <div className="staking-feature">
        {activeTab === 1 && (
          <>
            <StakingForm onShowLoading={handleShowLoading} isLoading={isLoading} />
            <ClaimForm isLoading={isLoading} />
          </>
        )}
        {activeTab === 2 && vaults && (
          <>
            <StakingFormOffChain
              redirectToHistory={redirectToHistory}
              onShowLoading={handleShowLoading}
              isLoading={isLoading}
              vaults={vaults}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Staking;
